import React from "react";

import type { MarkdownExtension, MarkdownExtensionNode } from "@/types";
import { xGroupWebReferenceMarkdownExtension } from "./webReference";

export interface XGroupKnowledgeReference {
  knowledgeNo: string;
  documentNo: string;
  shardingNos: string[];
  label: string;
  version?: string;
}

export const XGROUP_KNOWLEDGE_NO_ATTRIBUTES = [
  "knowledge-no",
  "knowledge-base-no",
  "workspace-no",
  "knowledgeno",
  "knowledgebaseno",
  "workspaceno",
] as const;
export const XGROUP_DOCUMENT_NO_ATTRIBUTES = [
  "document-no",
  "document-id",
  "documentno",
  "documentid",
] as const;
export const XGROUP_VERSION_ATTRIBUTES = [
  "version",
  "document-version",
  "documentversion",
] as const;
export const XGROUP_SHARDING_NO_ATTRIBUTES = [
  "sharding-nos",
  "sharding-no",
  "shardingnos",
  "shardingno",
] as const;

const AI_KB_REF_REGEX = /<ai-kb-ref\b([^>]*)>([\s\S]*?)<\/ai-kb-ref>/gi;
const XML_ATTRIBUTE_REGEX = /([a-z][a-z0-9-]*)\s*=\s*(["'])(.*?)\2/gi;

const getFirstAttribute = (
  attributes: Readonly<Record<string, string>>,
  names: readonly string[],
) => {
  for (const name of names) {
    const value = attributes[name];
    if (value?.trim()) return value.trim();
  }
  return "";
};

export const parseXGroupKnowledgeReference = ({
  attributes,
  text,
}: Pick<MarkdownExtensionNode, "attributes" | "text">): XGroupKnowledgeReference => {
  const rawShardingNos =
    getFirstAttribute(attributes, XGROUP_SHARDING_NO_ATTRIBUTES) || text;
  return {
    knowledgeNo: getFirstAttribute(attributes, XGROUP_KNOWLEDGE_NO_ATTRIBUTES),
    documentNo: getFirstAttribute(attributes, XGROUP_DOCUMENT_NO_ATTRIBUTES),
    shardingNos: rawShardingNos
      .split(/[,，\s]+/)
      .map((item) => item.trim())
      .filter(Boolean),
    label: text,
    version:
      getFirstAttribute(attributes, XGROUP_VERSION_ATTRIBUTES) || undefined,
  };
};

/** 从已完成的 Agent 文本中提取知识库引用，不在流式渲染期间产生副作用。 */
export const extractXGroupKnowledgeReferences = (
  text: string,
): XGroupKnowledgeReference[] => {
  if (!text) return [];
  const references: XGroupKnowledgeReference[] = [];
  const referenceRegex = new RegExp(
    AI_KB_REF_REGEX.source,
    AI_KB_REF_REGEX.flags,
  );
  let referenceMatch: RegExpExecArray | null;

  while ((referenceMatch = referenceRegex.exec(text)) !== null) {
    const attributes: Record<string, string> = {};
    const attributeRegex = new RegExp(
      XML_ATTRIBUTE_REGEX.source,
      XML_ATTRIBUTE_REGEX.flags,
    );
    let attributeMatch: RegExpExecArray | null;
    while ((attributeMatch = attributeRegex.exec(referenceMatch[1] || ""))) {
      attributes[(attributeMatch[1] || "").toLowerCase()] =
        attributeMatch[3] || "";
    }
    const label = (referenceMatch[2] || "")
      .replace(/<[^>]+>/g, "")
      .trim();
    references.push(
      parseXGroupKnowledgeReference({ attributes, text: label }),
    );
  }
  return references;
};

const renderStaticKnowledgeReference = (node: MarkdownExtensionNode) => {
  const reference = parseXGroupKnowledgeReference(node);
  return (
    <span
      aria-label={`知识库引用 ${reference.label || "引"}`}
      data-xadk-knowledge-reference="true"
      style={{
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 18,
        height: 18,
        margin: "0 3px",
        padding: "0 5px",
        border: "1px solid #e4e6eb",
        borderRadius: 9,
        background: "#f1f2f4",
        color: "#8a8f99",
        fontSize: 12,
        fontWeight: 500,
        lineHeight: "18px",
        verticalAlign: "1px",
      }}
    >
      {reference.label || "引"}
    </span>
  );
};

export const xGroupKnowledgeReferenceMarkdownExtension: MarkdownExtension = {
  tagName: "ai-kb-ref",
  allowedAttributes: [
    ...XGROUP_KNOWLEDGE_NO_ATTRIBUTES,
    ...XGROUP_DOCUMENT_NO_ATTRIBUTES,
    ...XGROUP_VERSION_ATTRIBUTES,
    ...XGROUP_SHARDING_NO_ATTRIBUTES,
  ],
  render: renderStaticKnowledgeReference,
};

export const xGroupMarkdownExtensions: readonly MarkdownExtension[] = [
  xGroupKnowledgeReferenceMarkdownExtension,
  xGroupWebReferenceMarkdownExtension,
];
